// src/components/receipts/format/index.jsx (ReceiptTemplate)

import React from "react";
import { Table } from "react-bootstrap";
import "./index.css";
import { ToWords } from "to-words";
import { format } from "date-fns";
import Skeleton from "react-loading-skeleton";

const ReceiptTemplate = ({
  isLoading,
  targetInvoiceRef,
  state,
  receiptData,
  businessProfile
}) => {
  const toWords = new ToWords();

  const safeNum = (v) => {
    const n = parseFloat(v);
    return Number.isNaN(n) ? 0 : n;
  };

  const taxRate = safeNum(state?.taxRate);

  const invoiceLines = Array.isArray(receiptData?.invoiceIDs)
    ? receiptData.invoiceIDs
    : [];

  let taxableValue = 0;
  let taxValue = 0;
  let overdueValue = 0;

  invoiceLines.forEach((item) => {
    const principal = safeNum(item.paidToPrincipal);
    const tax = safeNum(item.paidToTax);
    const overdueInterest = safeNum(item.paidToInterest);
    const overdueTax = safeNum(item.paidToOverdueTax);

    taxableValue += principal;
    taxValue += tax;
    overdueValue += overdueInterest + overdueTax;
  });

  if (invoiceLines.length === 0 && receiptData?.amountReceived && taxRate > 0) {
    const gross = safeNum(receiptData.amountReceived);
    const approxTax = gross - (gross * 100) / (taxRate + 100);
    taxableValue = gross - approxTax;
    taxValue = approxTax;
    overdueValue = 0;
  }

  const totalValue = taxableValue + taxValue + overdueValue;
  const amountInWords = toWords.convert(totalValue || 0, {
    currency: true
  });

  const isInterState =
    state?.partyDetails?.state &&
    state?.projectDetails?.propState &&
    state.partyDetails.state !== state.projectDetails.propState;

  const igstValue = isInterState ? taxValue : 0;
  const sgstValue = !isInterState ? taxValue / 2 : 0;
  const cgstValue = !isInterState ? taxValue / 2 : 0;

  const formatAmount = (n) =>
    safeNum(n).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const receiptDate = (() => {
    if (!receiptData?.date) return "";
    if (receiptData.date.seconds != null) {
      return new Date(receiptData.date.seconds * 1000);
    }
    if (receiptData.date instanceof Date) return receiptData.date;
    const d = new Date(receiptData.date);
    return Number.isNaN(d.getTime()) ? "" : d;
  })();

  return (
    <div className="border rounded">
      {isLoading ? (
        <div className="row p-3">
          <div className="col-md-12">
            <Skeleton
              height={"10vh"}
              baseColor="var(--primary-light)"
              highlightColor="linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))"
            />
            <Skeleton
              height={"10vh"}
              baseColor="var(--primary-light)"
              highlightColor="linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))"
              className="mt-3"
            />
            <Skeleton
              height={"70vh"}
              baseColor="var(--primary-light)"
              highlightColor="linear-gradient(to right, #f7f8ff , #A1E3F9)"
              className="mt-3"
            />
          </div>
        </div>
      ) : (
        <div className="print-wrapper" ref={targetInvoiceRef}>
          <div className="row pt-4 px-5">
            <div className="col-6">
              <h2 className="fw-bold pb-0 mb-0">Payment Receipt</h2>
            </div>
            <div className="col-6 text-end">
              <p className="mb-0">
                Date :{" "}
                <span className="fw-600">
                  {receiptDate
                    ? format(receiptDate, "dd MMM yyyy")
                    : "-"}
                </span>
              </p>
              <p className="mb-0">
                Receipt No. :{" "}
                <span className="fw-600">{receiptData?.id || "-"}</span>
              </p>
            </div>
          </div>

          <hr />

          <div className="row px-5">
            <div className="col-5 d-flex justify-content-start">
              <div className="text-start">
                <h6 className="fw-normal text-muted text-uppercase">
                  <small>Receipt from</small>
                </h6>
                <p className="mb-0 fw-600">
                  {state?.partyDetails?.partyName}
                </p>
                <p className="mb-0">
                  {state?.partyDetails?.address &&
                    `${state.partyDetails.address}, `}
                  {state?.partyDetails?.city &&
                    `${state.partyDetails.city}, `}
                  {state?.partyDetails?.state &&
                    `${state.partyDetails.state}, `}
                  {state?.partyDetails?.pincode}
                </p>
                <p className="mb-0">
                  {state?.partyDetails?.phoneNumber
                    ? `+91 ${state.partyDetails.phoneNumber}`
                    : ""}
                </p>
              </div>
            </div>
            <div className="col-2" />
            <div className="col-5 d-flex justify-content-end">
              <div className="text-end">
                <h6 className="fw-normal text-muted text-uppercase">
                  <small>Receipt to</small>
                </h6>
                <p className="mb-0 fw-600">
                  {businessProfile?.businessName || ""}
                </p>
                <p className="mb-0">
                  {businessProfile?.address &&
                    `${businessProfile.address}, `}
                  {businessProfile?.city &&
                    `${businessProfile.city}, `}
                  {businessProfile?.state &&
                    `${businessProfile.state}, `}
                  {businessProfile?.pincode}
                </p>
                <p className="mb-0">
                  {businessProfile?.phoneNumber
                    ? `+91 ${businessProfile.phoneNumber}`
                    : ""}
                </p>
              </div>
            </div>
          </div>

          <hr />

          <div className="row mt-0 px-5">
            <div className="col-4">
              <div className="text-muted fw-normal">
                <small>Property Description</small>
              </div>
              <div className="fw-normal">
                {state?.projectDetails?.propName || ""}
              </div>
              <div>
                {state?.projectDetails?.propNumber &&
                  `${state.projectDetails.propNumber}, `}
                {state?.projectDetails?.propCity &&
                  `${state.projectDetails.propCity}, `}
                {state?.projectDetails?.propState &&
                  `${state.projectDetails.propState}, `}
                {state?.projectDetails?.propPincode}
              </div>
            </div>
            <div className="col-4 text-center">
              <div className="text-muted fw-normal">
                <small>Booking No.</small>
              </div>
              <div className="fw-normal">
                {receiptData?.bookingID || ""}
              </div>
            </div>
            <div className="col-4 text-end">
              <div className="text-muted fw-normal">
                <small>Unit</small>
              </div>
              <div className="fw-normal">
                {state?.projectDetails?.bookedUnit?.unitName && (
                  <>
                    {state.projectDetails.bookedUnit.unitName}{" "}
                    {state.projectDetails.bookedUnit.unitArea &&
                      `(${state.projectDetails.bookedUnit.unitArea} ${state.projectDetails.propUnitMeasure || ""
                      })`}
                  </>
                )}
              </div>
              <div className="fw-normal mt-2">
                <small>
                  <span className="me-1 border rounded px-3 py-1 bg-primary-light">
                    Floor -{" "}
                    <span>
                      {state?.projectDetails?.bookedUnit?.unitFloor || ""}
                    </span>
                  </span>
                  <span className="ms-1 border rounded px-3 py-1 bg-primary-light">
                    Unit No. -{" "}
                    <span>
                      {state?.projectDetails?.bookedUnit?.unitNumber ||
                        ""}
                    </span>
                  </span>
                </small>
              </div>
            </div>
          </div>

          <div className="mt-4 px-5">
            <Table bordered className="mb-0">
              <thead>
                <tr className="text-center border-bottom">
                  <th className="fw-normal text-muted text-start py-2 px-3">
                    <small>Particular</small>
                  </th>
                  <th className="fw-normal text-muted py-2 px-3">
                    <small>Invoice No.</small>
                  </th>
                  <th className="fw-normal text-muted py-2 px-3">
                    <small>Taxable</small>
                  </th>
                  <th className="fw-normal text-muted py-2 px-3">
                    <small>Tax ({taxRate}%)</small>
                  </th>
                  <th className="fw-normal text-muted py-2 px-3">
                    <small>Overdue Charges</small>
                  </th>
                  <th className="fw-normal text-muted text-end py-2 px-3">
                    <small>Amount</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoiceLines.map((invItem, index) => {
                  const principal = safeNum(invItem.paidToPrincipal);
                  const tax = safeNum(invItem.paidToTax);
                  const overdueInterest = safeNum(invItem.paidToInterest);
                  const overdueTax = safeNum(invItem.paidToOverdueTax);

                  const itemTaxable = principal;
                  const itemTax = tax;
                  const itemOverdue = overdueInterest + overdueTax;
                  const itemTotalAmount =
                    principal + tax + itemOverdue;

                  return (
                    <tr key={index} className="text-center">
                      <td className="text-start py-2 px-3">
                        <p className="mb-1">
                          {invItem?.name || ""}
                        </p>
                        <p className="mb-0 fw-light text-muted lh-1">
                          <small>{invItem?.description || ""}</small>
                        </p>
                      </td>
                      <td className="py-2 px-3">{invItem.id}</td>
                      <td className="py-2 px-3">
                        {formatAmount(itemTaxable)}
                      </td>
                      <td className="py-2 px-3">
                        {formatAmount(itemTax)}
                      </td>
                      <td className="py-2 px-3">
                        {formatAmount(itemOverdue)}
                      </td>
                      <td className="text-end py-2 px-3">
                        {formatAmount(itemTotalAmount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="row mt-3 px-5">
            <div className="col-6">
              {receiptData?.paymentDetails && (
                <div className="mb-3 me-5 border p-3">
                  <h6 className="fw-normal mb-2 text-muted">
                    <small>Payment Details</small>
                  </h6>
                  <p className="text-justify mb-0 lh-1">
                    <small>
                      Mode of Payment -{" "}
                      <span className="fw-600">
                        {receiptData.paymentDetails.paymentMode || ""}
                      </span>
                    </small>
                  </p>
                  {receiptData.description ? (
                    <p className="text-justify mb-0">
                      <small>
                        Remark -{" "}
                        <span className="fw-600">
                          {receiptData.description}
                        </span>
                      </small>
                    </p>
                  ) : null}
                </div>
              )}
            </div>

            <div className="col-6">
              <div className="d-flex align-items-center justify-content-between">
                <div className="fw-600">Taxable Amount</div>
                <div className="fw-bold text-end">
                  {formatAmount(taxableValue)}
                </div>
              </div>

              {isInterState ? (
                <div className="mt-2">
                  <div className="row">
                    <div className="col-8">IGST @ {taxRate}%</div>
                    <div className="col-4 text-end">
                      {formatAmount(igstValue)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <div className="row">
                    <div className="col-8">
                      SGST @ {taxRate / 2}%
                    </div>
                    <div className="col-4 text-end">
                      {formatAmount(sgstValue)}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-8">
                      CGST @ {taxRate / 2}%
                    </div>
                    <div className="col-4 text-end">
                      {formatAmount(cgstValue)}
                    </div>
                  </div>
                </div>
              )}

              {overdueValue > 0 && (
                <div className="mt-2">
                  <div className="row">
                    <div className="col-8">
                      Overdue Charges (incl. tax)
                    </div>
                    <div className="col-4 text-end">
                      {formatAmount(overdueValue)}
                    </div>
                  </div>
                </div>
              )}

              <hr className="mb-0" />
              <div className="d-flex align-items-center justify-content-between my-2">
                <h5 className="fw-600 mb-0">Total Amount</h5>
                <h5 className="fw-600 mb-0 text-end">
                  {formatAmount(totalValue)}
                </h5>
              </div>
              <hr className="mt-0" />
              <div className="mt-0 d-flex align-items-center justify-content-end">
                <small>
                  <span className="text-end">{amountInWords}</span>
                </small>
              </div>
            </div>

            <div className="col-6 offset-6 mt-3 mb-3">
              <div className="d-flex align-items-end justify-content-end">
                <div className="mb-0 mt-4 py-3">
                  Authorized Signature
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptTemplate;